import { type FC, useState, useEffect } from "react";
import api from "../services/api";
import { useData } from "../context/DataContext";
import { getCategoryBadge } from "../utils/utils";
import type { Page, CategoryType, OneShotGroupDto, QrCode, ApiResponse } from "../types";
import QrCodeListDisplayModal from "../components/QrCodeListDisplayModal";

const ClientOneShotGroupsPage: FC = () => {
    const [oneShotGroupsPage, setOneShotGroupsPage] = useState<Page<OneShotGroupDto> | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);

    const [activeCategory, setActiveCategory] = useState<number>(-1);
    const { categories } = useData();

    const [qrCodesForModal, setQrCodesForModal] = useState<QrCode[] | null>(null);
    const [isFetchingGroupDetails, setIsFetchingGroupDetails] = useState(false);

    useEffect(() => {
        const fetchClientOneShotGroups = async (page: number, categoryId: number) => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await api.get<ApiResponse<Page<OneShotGroupDto>>>(`/api/oneshot/getAll`, {
                    params: {
                        page: page,
                        size: 15,
                        idCategory: categoryId,
                    },
                });

                if (response.data.status) {
                    setOneShotGroupsPage(response.data.data);
                } else {
                    throw new Error(response.data.message || "Impossibile caricare i gruppi One-Shot.");
                }
            } catch (err: any) {
                setError(err.message || "Si è verificato un errore di rete.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchClientOneShotGroups(currentPage, activeCategory);
    }, [currentPage, activeCategory]);

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentPage(0);
        setActiveCategory(Number(e.target.value));
    };

    const handleViewGroupDetails = async (group: OneShotGroupDto) => {
        setIsFetchingGroupDetails(true);
        setError(null);
        try {
            // Endpoint per recuperare i QR code specifici di un gruppo one-shot
            const response = await api.get<ApiResponse<QrCode[]>>(`/api/qrcodes/getByOneShot/${group.id}`);
            if (response.data.status) {
                setQrCodesForModal(response.data.data);
            } else {
                throw new Error(response.data.message || "Impossibile caricare i QR code del gruppo.");
            }
        } catch (err: any) {
            setError(err.message || "Si è verificato un errore durante il recupero dei dettagli del gruppo.");
        } finally {
            setIsFetchingGroupDetails(false);
        }
    };

    const handleCloseModal = () => {
        setQrCodesForModal(null);
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">I Miei Gruppi One-Shot</h1>
                    <p className="text-slate-400">Elenco di tutti i gruppi One-Shot a te associati.</p>
                </div>
                <select onChange={handleCategoryChange} value={activeCategory} className="bg-slate-700/50 border border-slate-600 px-4 py-2 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 self-start md:self-center">
                    <option value={-1}>Tutte le categorie</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="text-xs text-slate-400 uppercase bg-slate-800">
                        <tr>
                            <th className="p-4">Nome Gruppo</th>
                            <th className="p-4">Categoria</th>
                            <th className="p-4 hidden sm:table-cell">Cliente</th> {/* Il cliente sarà sempre se stesso per un client page */}
                            <th className="p-4">QR Code Totali</th>
                            <th className="p-4">Scansioni Effettuate</th>
                            <th className="p-4 text-right">Azioni</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                        {isLoading ? (
                            <tr><td colSpan={6} className="text-center p-8 text-slate-400">Caricamento...</td></tr>
                        ) : error ? (
                            <tr><td colSpan={6} className="text-center p-8 text-red-400">{error}</td></tr>
                        ) : !oneShotGroupsPage || oneShotGroupsPage.content.length === 0 ? (
                            <tr><td colSpan={6} className="text-center p-8 text-slate-400">Nessun gruppo One-Shot trovato.</td></tr>
                        ) : (
                            oneShotGroupsPage.content.map(group => (
                                <tr key={group.id} className="hover:bg-slate-800 transition-colors">
                                    <td className="p-4 text-white font-medium">{group.name}</td>
                                    <td className="p-4">{getCategoryBadge(group.category as CategoryType)}</td>
                                    <td className="p-4 text-slate-300 hidden sm:table-cell">{group.client || 'N/A'}</td>
                                    <td className="p-4 text-slate-300">{group.count}</td>
                                    <td className="p-4 text-slate-300">{group.scanned}</td>
                                    <td className="p-4 text-right">
                                        <button
                                            onClick={() => handleViewGroupDetails(group)}
                                            disabled={isFetchingGroupDetails}
                                            className="text-sky-400 hover:text-sky-300 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isFetchingGroupDetails ? 'Caricamento...' : 'Visualizza'}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
                {oneShotGroupsPage && oneShotGroupsPage.totalPages > 1 && (
                    <div className="p-4 border-t border-slate-700 flex justify-between items-center text-sm text-slate-400">
                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={oneShotGroupsPage.number === 0 || isLoading || isFetchingGroupDetails} className="px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed">Precedente</button>
                        <span>Pagina {oneShotGroupsPage.number + 1} di {oneShotGroupsPage.totalPages}</span>
                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={oneShotGroupsPage.number >= oneShotGroupsPage.totalPages - 1 || isLoading || isFetchingGroupDetails} className="px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed">Successiva</button>
                    </div>
                )}
            </div>

            {qrCodesForModal && (
                <QrCodeListDisplayModal
                    qrCodes={qrCodesForModal}
                    onClose={handleCloseModal}
                    download={true}
                />
            )}
        </div>
    );
};

export default ClientOneShotGroupsPage;